import { type KeyboardEvent, type ReactNode, useMemo } from 'react';
import type { Object3D } from 'three';

import { isMhrScene } from '@cartwhl/3d/gltf/utils';

export type KeyPoseStickFigureSkeletonType = 'axel' | 'mhr';

export type KeyPoseStickFigureJointId =
  | 'head'
  | 'neck'
  | 'spine3'
  | 'spine2'
  | 'spine1'
  | 'spine0'
  | 'pelvis'
  | 'left_collar'
  | 'right_collar'
  | 'left_shoulder'
  | 'right_shoulder'
  | 'left_elbow'
  | 'right_elbow'
  | 'left_wrist'
  | 'right_wrist'
  | 'left_hip'
  | 'right_hip'
  | 'left_knee'
  | 'right_knee'
  | 'left_ankle'
  | 'right_ankle'
  | 'left_foot'
  | 'right_foot';

export type KeyPoseStickFigureMode = 'fk' | 'ik';

interface StickFigureJoint {
  id: KeyPoseStickFigureJointId;
  x: number;
  y: number;
  label: string;
}

interface StickFigureSegment {
  from: KeyPoseStickFigureJointId;
  to: KeyPoseStickFigureJointId;
}

interface StickFigureSkeletonConfig {
  joints: readonly StickFigureJoint[];
  segments: readonly StickFigureSegment[];
  selectableJointIdsByMode: Record<
    KeyPoseStickFigureMode,
    readonly KeyPoseStickFigureJointId[]
  >;
}

interface KeyPoseStickFigureToolOption {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}

interface KeyPoseStickFigureSelectorProps {
  hoveredJointId: KeyPoseStickFigureJointId | null;
  mode: KeyPoseStickFigureMode;
  selectedJointId: KeyPoseStickFigureJointId | null;
  skeletonType?: KeyPoseStickFigureSkeletonType;
  toolOptions: readonly KeyPoseStickFigureToolOption[];
  onHoveredJointChange: (jointId: KeyPoseStickFigureJointId | null) => void;
  onSelectedJointChange: (jointId: KeyPoseStickFigureJointId) => void;
}

const KEY_POSE_SELECTABLE_LIMB_JOINT_IDS = [
  'left_collar',
  'right_collar',
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
  'left_hip',
  'right_hip',
  'left_knee',
  'right_knee',
  'left_ankle',
  'right_ankle',
  'left_foot',
  'right_foot',
] as const satisfies readonly KeyPoseStickFigureJointId[];

const AXEL_FK_SPINE_JOINT_IDS = [
  'spine3',
  'spine2',
  'spine1',
] as const satisfies readonly KeyPoseStickFigureJointId[];

const MHR_FK_SPINE_JOINT_IDS = [
  'spine3',
  'spine2',
  'spine1',
  'spine0',
] as const satisfies readonly KeyPoseStickFigureJointId[];

function buildFkSelectableJointIds(
  spineJointIds: readonly KeyPoseStickFigureJointId[],
) {
  return [
    'head',
    'neck',
    ...spineJointIds,
    'pelvis',
    ...KEY_POSE_SELECTABLE_LIMB_JOINT_IDS,
  ] as const satisfies readonly KeyPoseStickFigureJointId[];
}

const KEY_POSE_SELECTABLE_JOINT_IDS_BY_MODE: Record<
  KeyPoseStickFigureMode,
  readonly KeyPoseStickFigureJointId[]
> = {
  fk: buildFkSelectableJointIds(AXEL_FK_SPINE_JOINT_IDS),
  ik: [
    'head',
    'pelvis',
    'left_wrist',
    'right_wrist',
    'left_ankle',
    'right_ankle',
  ],
};

const AXEL_SELECTABLE_JOINT_IDS_BY_MODE: Record<
  KeyPoseStickFigureMode,
  readonly KeyPoseStickFigureJointId[]
> = {
  ...KEY_POSE_SELECTABLE_JOINT_IDS_BY_MODE,
  fk: buildFkSelectableJointIds(AXEL_FK_SPINE_JOINT_IDS),
};

const MHR_SELECTABLE_JOINT_IDS_BY_MODE: Record<
  KeyPoseStickFigureMode,
  readonly KeyPoseStickFigureJointId[]
> = {
  ...KEY_POSE_SELECTABLE_JOINT_IDS_BY_MODE,
  fk: buildFkSelectableJointIds(MHR_FK_SPINE_JOINT_IDS),
};

const AXEL_STICK_FIGURE_JOINTS = [
  { id: 'head', x: 38, y: 0, label: 'Head' },
  { id: 'neck', x: 38, y: 27, label: 'Neck' },
  { id: 'spine3', x: 38, y: 45, label: 'Spine 3' },
  { id: 'spine2', x: 38, y: 63, label: 'Spine 2' },
  { id: 'spine1', x: 38, y: 81, label: 'Spine 1' },
  { id: 'pelvis', x: 38, y: 99, label: 'Pelvis' },
  { id: 'left_collar', x: 55, y: 27, label: 'Left clavicle' },
  { id: 'right_collar', x: 21, y: 27, label: 'Right clavicle' },
  { id: 'left_shoulder', x: 69, y: 27, label: 'Left shoulder' },
  { id: 'right_shoulder', x: 7, y: 27, label: 'Right shoulder' },
  { id: 'left_elbow', x: 78, y: 55, label: 'Left elbow' },
  { id: 'right_elbow', x: -2, y: 55, label: 'Right elbow' },
  { id: 'left_wrist', x: 84, y: 88, label: 'Left wrist' },
  { id: 'right_wrist', x: -8, y: 88, label: 'Right wrist' },
  { id: 'left_hip', x: 51, y: 116, label: 'Left hip' },
  { id: 'right_hip', x: 25, y: 116, label: 'Right hip' },
  { id: 'left_knee', x: 51, y: 152, label: 'Left knee' },
  { id: 'right_knee', x: 25, y: 152, label: 'Right knee' },
  { id: 'left_ankle', x: 51, y: 176, label: 'Left ankle' },
  { id: 'right_ankle', x: 25, y: 176, label: 'Right ankle' },
  { id: 'left_foot', x: 53, y: 195, label: 'Left foot' },
  { id: 'right_foot', x: 23, y: 195, label: 'Right foot' },
] as const satisfies readonly StickFigureJoint[];

const AXEL_STICK_FIGURE_SEGMENTS = [
  { from: 'head', to: 'neck' },
  { from: 'neck', to: 'spine3' },
  { from: 'spine3', to: 'spine2' },
  { from: 'spine2', to: 'spine1' },
  { from: 'spine1', to: 'pelvis' },
  { from: 'neck', to: 'left_collar' },
  { from: 'neck', to: 'right_collar' },
  { from: 'left_collar', to: 'left_shoulder' },
  { from: 'right_collar', to: 'right_shoulder' },
  { from: 'left_shoulder', to: 'left_elbow' },
  { from: 'right_shoulder', to: 'right_elbow' },
  { from: 'left_elbow', to: 'left_wrist' },
  { from: 'right_elbow', to: 'right_wrist' },
  { from: 'pelvis', to: 'left_hip' },
  { from: 'pelvis', to: 'right_hip' },
  { from: 'left_hip', to: 'left_knee' },
  { from: 'right_hip', to: 'right_knee' },
  { from: 'left_knee', to: 'left_ankle' },
  { from: 'right_knee', to: 'right_ankle' },
  { from: 'left_ankle', to: 'left_foot' },
  { from: 'right_ankle', to: 'right_foot' },
] as const satisfies readonly StickFigureSegment[];

const MHR_STICK_FIGURE_JOINTS = [
  { id: 'head', x: 38, y: 0, label: 'Head' },
  { id: 'neck', x: 38, y: 27, label: 'Neck' },
  { id: 'spine3', x: 38, y: 45, label: 'Spine 3' },
  { id: 'spine2', x: 38, y: 63, label: 'Spine 2' },
  { id: 'spine1', x: 38, y: 81, label: 'Spine 1' },
  { id: 'spine0', x: 38, y: 99, label: 'Spine 0' },
  { id: 'pelvis', x: 38, y: 117, label: 'Pelvis' },
  { id: 'left_collar', x: 55, y: 27, label: 'Left clavicle' },
  { id: 'right_collar', x: 21, y: 27, label: 'Right clavicle' },
  { id: 'left_shoulder', x: 69, y: 27, label: 'Left upper arm' },
  { id: 'right_shoulder', x: 7, y: 27, label: 'Right upper arm' },
  { id: 'left_elbow', x: 78, y: 55, label: 'Left lower arm' },
  { id: 'right_elbow', x: -2, y: 55, label: 'Right lower arm' },
  { id: 'left_wrist', x: 84, y: 88, label: 'Left wrist' },
  { id: 'right_wrist', x: -8, y: 88, label: 'Right wrist' },
  { id: 'left_hip', x: 51, y: 134, label: 'Left upper leg' },
  { id: 'right_hip', x: 25, y: 134, label: 'Right upper leg' },
  { id: 'left_knee', x: 51, y: 170, label: 'Left lower leg' },
  { id: 'right_knee', x: 25, y: 170, label: 'Right lower leg' },
  { id: 'left_ankle', x: 51, y: 194, label: 'Left ankle' },
  { id: 'right_ankle', x: 25, y: 194, label: 'Right ankle' },
  { id: 'left_foot', x: 53, y: 213, label: 'Left toe' },
  { id: 'right_foot', x: 23, y: 213, label: 'Right toe' },
] as const satisfies readonly StickFigureJoint[];

const MHR_STICK_FIGURE_SEGMENTS = [
  { from: 'head', to: 'neck' },
  { from: 'neck', to: 'spine3' },
  { from: 'spine3', to: 'spine2' },
  { from: 'spine2', to: 'spine1' },
  { from: 'spine1', to: 'spine0' },
  { from: 'spine0', to: 'pelvis' },
  { from: 'neck', to: 'left_collar' },
  { from: 'neck', to: 'right_collar' },
  { from: 'left_collar', to: 'left_shoulder' },
  { from: 'right_collar', to: 'right_shoulder' },
  { from: 'left_shoulder', to: 'left_elbow' },
  { from: 'right_shoulder', to: 'right_elbow' },
  { from: 'left_elbow', to: 'left_wrist' },
  { from: 'right_elbow', to: 'right_wrist' },
  { from: 'pelvis', to: 'left_hip' },
  { from: 'pelvis', to: 'right_hip' },
  { from: 'left_hip', to: 'left_knee' },
  { from: 'right_hip', to: 'right_knee' },
  { from: 'left_knee', to: 'left_ankle' },
  { from: 'right_knee', to: 'right_ankle' },
  { from: 'left_ankle', to: 'left_foot' },
  { from: 'right_ankle', to: 'right_foot' },
] as const satisfies readonly StickFigureSegment[];

const STICK_FIGURE_CONFIG_BY_SKELETON_TYPE: Record<
  KeyPoseStickFigureSkeletonType,
  StickFigureSkeletonConfig
> = {
  axel: {
    joints: AXEL_STICK_FIGURE_JOINTS,
    segments: AXEL_STICK_FIGURE_SEGMENTS,
    selectableJointIdsByMode: AXEL_SELECTABLE_JOINT_IDS_BY_MODE,
  },
  mhr: {
    joints: MHR_STICK_FIGURE_JOINTS,
    segments: MHR_STICK_FIGURE_SEGMENTS,
    selectableJointIdsByMode: MHR_SELECTABLE_JOINT_IDS_BY_MODE,
  },
};

export { KEY_POSE_SELECTABLE_JOINT_IDS_BY_MODE };

function getStickFigureConfig(skeletonType: KeyPoseStickFigureSkeletonType) {
  return STICK_FIGURE_CONFIG_BY_SKELETON_TYPE[skeletonType];
}

function getJointById(config: StickFigureSkeletonConfig) {
  return config.joints.reduce(
    (acc, joint) => ({ ...acc, [joint.id]: joint }),
    {} as Record<KeyPoseStickFigureJointId, StickFigureJoint>,
  );
}

function getSelectableJointIdSet(
  skeletonType: KeyPoseStickFigureSkeletonType,
  mode: KeyPoseStickFigureMode,
) {
  return new Set<KeyPoseStickFigureJointId>(
    getStickFigureConfig(skeletonType).selectableJointIdsByMode[mode],
  );
}

export function resolveKeyPoseStickFigureSkeletonType(
  scene: Object3D | null | undefined,
): KeyPoseStickFigureSkeletonType {
  if (scene && isMhrScene(scene)) {
    return 'mhr';
  }

  if (
    scene &&
    (scene.getObjectByName('c_head') ||
      scene.getObjectByName('l_wrist') ||
      scene.getObjectByName('root'))
  ) {
    return 'mhr';
  }

  return 'axel';
}

export default function KeyPoseStickFigureSelector({
  hoveredJointId,
  mode,
  selectedJointId,
  skeletonType = 'axel',
  toolOptions,
  onHoveredJointChange,
  onSelectedJointChange,
}: KeyPoseStickFigureSelectorProps) {
  const stickFigureConfig = useMemo(
    () => getStickFigureConfig(skeletonType),
    [skeletonType],
  );
  const jointById = useMemo(
    () => getJointById(stickFigureConfig),
    [stickFigureConfig],
  );
  const selectableJointIdSet = useMemo(
    () => getSelectableJointIdSet(skeletonType, mode),
    [mode, skeletonType],
  );
  const joints = useMemo(
    () =>
      stickFigureConfig.joints.filter((joint) =>
        selectableJointIdSet.has(joint.id),
      ),
    [selectableJointIdSet, stickFigureConfig],
  );
  const viewBoxHeight = 176;

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="dark:bg-gray-90/25 dark:hover:bg-gray-90/45 relative h-[214px] w-full overflow-hidden rounded-lg bg-white/25 hover:bg-white/45">
        <svg
          aria-label="Select key pose joint"
          className="absolute top-1/2 left-1/2 w-[92px] -translate-x-1/2 -translate-y-1/2 overflow-visible"
          role="group"
          style={{ height: viewBoxHeight }}
          viewBox={`-8 0 92 200`}
        >
          {stickFigureConfig.segments.map((segment) => {
            const from = jointById[segment.from];
            const to = jointById[segment.to];

            return (
              <line
                key={`${segment.from}-${segment.to}`}
                className="stroke-gray-45 dark:stroke-gray-45"
                strokeLinecap="round"
                strokeWidth={3.5}
                x1={from.x}
                x2={to.x}
                y1={from.y}
                y2={to.y}
              />
            );
          })}

          {joints.map((joint) => {
            const active = joint.id === selectedJointId;
            const hovered = joint.id === hoveredJointId;

            return (
              <g
                key={joint.id}
                aria-label={joint.label}
                role="button"
                tabIndex={0}
                className="cursor-pointer outline-none"
                onClick={() => onSelectedJointChange(joint.id)}
                onFocus={() => onHoveredJointChange(joint.id)}
                onKeyDown={(event) => {
                  handleJointKeyDown(event, () =>
                    onSelectedJointChange(joint.id),
                  );
                }}
                onPointerEnter={() => onHoveredJointChange(joint.id)}
                onPointerLeave={() => onHoveredJointChange(null)}
              >
                <title>{joint.label}</title>
                <circle cx={joint.x} cy={joint.y} fill="transparent" r={10} />
                <circle
                  className={
                    active
                      ? 'fill-orange stroke-white'
                      : hovered
                        ? 'stroke-blue fill-white'
                        : 'fill-blue stroke-white'
                  }
                  cx={joint.x}
                  cy={joint.y}
                  r={5.5}
                  strokeWidth={1.5}
                />
              </g>
            );
          })}
        </svg>
      </div>

      <div className="flex h-[30px] w-full gap-1">
        {toolOptions.map((option) => (
          <button
            key={option.label}
            type="button"
            aria-label={option.label}
            title={option.label}
            className={`focus-visible text-primary flex flex-1 cursor-pointer items-center justify-center rounded-lg transition-all active:scale-[0.98] ${
              option.active
                ? 'dark:bg-gray-90 bg-white'
                : 'dark:bg-gray-90/25 dark:hover:bg-gray-90/45 bg-white/25 hover:bg-white/45'
            }`}
            onClick={option.onClick}
          >
            <span className="flex size-5 items-center justify-center">
              {option.icon}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function isKeyPoseStickFigureJointSelectable(
  skeletonType: KeyPoseStickFigureSkeletonType,
  mode: KeyPoseStickFigureMode,
  jointId: KeyPoseStickFigureJointId,
) {
  return getSelectableJointIdSet(skeletonType, mode).has(jointId);
}

function handleJointKeyDown(
  event: KeyboardEvent<SVGGElement>,
  selectJoint: () => void,
) {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }

  event.preventDefault();
  selectJoint();
}
